import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet, Plus, Minus, X } from 'lucide-react';
import { useActivePortfolioId } from '@/components/PortfolioSelector';
import { useIsReadOnlyAllView } from '@/lib/useIsReadOnlyAllView';
import AllViewReadOnlyBanner from '@/components/AllViewReadOnlyBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CashCard — Cash Balance ribbon with Add Outside / Add Inside /
 * Withdraw Outside / Withdraw Inside / Remove actions.
 *
 * Round 91 (Feb 2026) — extracted from PortfolioSummary.js into its
 * own file so CapitalTrackerCard can embed it without a circular
 * import. Behaviour unchanged.
 *
 * Props:
 *   embedded — when true, renders without its own outer card chrome
 *              (no border / no bg-card / no padding wrapper). The
 *              parent supplies the card.
 */
export default function CashCard({ embedded = false }) {
  const [balance, setBalance] = useState(0);
  // Round 92o++++++++N++++++++++ (Jun 2026) — Multi-portfolio:
  // when the user has a specific portfolio selected (Plus tier),
  // surface a small "shared across portfolios" hint so the cash
  // number doesn't look like it belongs ONLY to that portfolio.
  const { activeId: activePortfolioId } = useActivePortfolioId();
  const showGlobalHint = false;
  // Plus-tier All Portfolios view is read-only — no cash transactions
  // allowed from the summary. The action buttons + form are hidden
  // and a small banner points the user back to the dropdown.
  const { isReadOnly: isAllReadOnly } = useIsReadOnlyAllView();
  // Mode controls which form is open below the cash row:
  //   'deposit-outside'   → Add Outside Cash (contribution)
  //   'deposit-inside'    → Add Inside Cash (sale proceeds / dividend)
  //   'withdraw-outside'  → Withdraw Outside Cash (counts as withdrawal)
  //   'withdraw-inside'   → Withdraw Inside Cash (draw down inside, no withdrawal-effect)
  //   'remove'            → Remove Cash (typo / mistake — does NOT
  //                                       count as withdrawal)
  const [mode, setMode] = useState(null);
  const [amount, setAmount] = useState('');
  const [removeSource, setRemoveSource] = useState('outside'); // outside | inside | split
  const [removeOutsideAmount, setRemoveOutsideAmount] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    const params = activePortfolioId ? { portfolio_id: activePortfolioId } : {};
    axios.get(`${API}/portfolio/cash`, { params, withCredentials: true })
      .then(res => setBalance(Number(res.data?.cash_balance) || 0))
      .catch(() => {});
  }, [activePortfolioId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh whenever cash-changed fires (sells from any page)
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener('cash-changed', handler);
    return () => window.removeEventListener('cash-changed', handler);
  }, [refresh]);

  const reset = () => {
    setAmount('');
    setRemoveSource('outside');
    setRemoveOutsideAmount('');
    setMode(null);
    setErr('');
  };

  const submit = async () => {
    const n = Number(amount);
    if (!n || n <= 0) { setErr('Enter an amount'); return; }
    setBusy(true); setErr('');
    try {
      let endpoint;
      let payload;
      if (mode === 'deposit-outside') {
        endpoint = 'deposit';
        payload = { amount: n, cash_source: 'outside' };
      } else if (mode === 'deposit-inside') {
        endpoint = 'deposit';
        payload = { amount: n, cash_source: 'inside' };
      } else if (mode === 'withdraw-outside') {
        endpoint = 'withdraw';
        payload = { amount: n, cash_source: 'outside' };
      } else if (mode === 'withdraw-inside') {
        endpoint = 'withdraw';
        payload = { amount: n, cash_source: 'inside' };
      } else if (mode === 'remove') {
        endpoint = 'remove';
        payload = {
          amount: n,
          cash_source: removeSource,
          outside_amount: removeSource === 'split'
            ? Math.max(0, Math.min(n, Number(removeOutsideAmount) || 0))
            : 0,
        };
      } else {
        setBusy(false); return;
      }
      const { data } = await axios.post(
        `${API}/portfolio/cash/${endpoint}`,
        payload,
        { withCredentials: true }
      );
      setBalance(Number(data.cash_balance) || 0);
      reset();
      window.dispatchEvent(new Event('cash-changed'));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed');
    }
    setBusy(false);
  };

  // Each button toggles its own mode; clicking again closes the form.
  const btn = (key, label, Icon, classes, testId) => (
    <button
      onClick={() => { if (mode === key) { reset(); } else { setMode(key); setAmount(''); setErr(''); } }}
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
        mode === key ? classes.active : classes.idle
      }`}
      data-testid={testId}
      title={classes.title}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  const submitLabel = mode === 'deposit-outside' ? 'Add Outside'
    : mode === 'deposit-inside' ? 'Add Inside'
    : mode === 'withdraw-outside' ? 'Withdraw Outside'
    : mode === 'withdraw-inside' ? 'Withdraw Inside'
    : mode === 'remove' ? 'Remove'
    : '';

  return (
    <div
      className={embedded
        ? 'pt-1'
        : 'rounded-lg border bg-card p-4 mb-3'}
      data-testid="portfolio-cash-card"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <Wallet className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="text-xs font-extrabold text-foreground uppercase tracking-wider" data-testid="cash-balance-label">
              Cash Balance
              {showGlobalHint && (
                <span className="ml-1.5 font-normal text-[10px] normal-case tracking-normal text-muted-foreground" title="Cash lives at the household level — the same balance shows on every portfolio. Buys and sells in any portfolio draw from this single pool.">
                  · shared across portfolios
                </span>
              )}
            </p>
            <p className="text-xl font-bold font-mono text-emerald-700 dark:text-emerald-400" data-testid="cash-balance-value">
              ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {isAllReadOnly ? (
            <AllViewReadOnlyBanner verb="record cash transactions" compact />
          ) : (
            <>
              {btn('deposit-outside', 'Add Outside', Plus, {
                active: 'bg-emerald-600 text-white',
                idle: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/60',
                title: 'Add fresh outside money (contribution). Counts toward Total Contributions.',
              }, 'cash-deposit-outside-btn')}
              {btn('deposit-inside', 'Add Inside', Plus, {
                active: 'bg-violet-600 text-white',
                idle: 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-950/60',
                title: 'Add inside cash (stock sale proceeds or dividend cash). Does NOT count as a contribution.',
              }, 'cash-deposit-inside-btn')}
              {btn('withdraw-outside', 'Withdraw Outside', Minus, {
                active: 'bg-red-600 text-white',
                idle: 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-950/60',
                title: 'Withdraw OUTSIDE cash (principal coming home). Reduces Net Contributions.',
              }, 'cash-withdraw-outside-btn')}
              {btn('withdraw-inside', 'Withdraw Inside', Minus, {
                active: 'bg-orange-600 text-white',
                idle: 'bg-orange-50 dark:bg-orange-950/40 text-orange-700 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-950/60',
                title: 'Withdraw INSIDE cash (dividends or sale proceeds being drawn down). Does NOT reduce Net Contributions.',
              }, 'cash-withdraw-inside-btn')}
              {btn('remove', 'Remove', X, {
                active: 'bg-amber-600 text-white',
                idle: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-950/60',
                title: 'Remove cash without it counting as a Withdrawal — useful for fixing typos or undoing mis-entered cash.',
              }, 'cash-remove-btn')}
            </>
          )}
        </div>
      </div>

      {mode && !isAllReadOnly && (
        <div className="mt-3 pt-3 border-t flex flex-wrap items-end gap-2" data-testid={`cash-${mode}-form`}>
          <div>
            <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Amount $</label>
            <input
              type="number" min="0" step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-32 px-2 py-1 text-sm font-mono rounded border bg-background"
              placeholder="0.00"
              data-testid="cash-amount-input"
              autoFocus
            />
          </div>
          {mode === 'remove' && (
            <div className="flex items-end gap-2">
              <div>
                <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Source</label>
                <div className="flex rounded border overflow-hidden text-[11px] font-semibold" data-testid="remove-source-toggle">
                  {[
                    { key: 'outside', label: 'Outside' },
                    { key: 'inside', label: 'Inside' },
                    { key: 'split', label: 'Split' },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setRemoveSource(opt.key)}
                      className={`px-2 py-1 ${
                        removeSource === opt.key
                          ? 'bg-amber-600 text-white'
                          : 'bg-background text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid={`remove-source-${opt.key}`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {removeSource === 'split' && (
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Outside $ (rest = inside)</label>
                  <input
                    type="number" min="0" step="any"
                    value={removeOutsideAmount}
                    onChange={(e) => setRemoveOutsideAmount(e.target.value)}
                    className="w-28 px-2 py-1 text-sm font-mono rounded border bg-background"
                    placeholder="0.00"
                    data-testid="remove-outside-amount-input"
                  />
                </div>
              )}
            </div>
          )}
          <button
            onClick={submit}
            disabled={busy}
            className={`px-3 py-1.5 rounded text-xs font-semibold text-white disabled:opacity-60 ${
              mode === 'deposit-outside' ? 'bg-emerald-600 hover:bg-emerald-700'
                : mode === 'deposit-inside' ? 'bg-violet-600 hover:bg-violet-700'
                : mode === 'withdraw-outside' ? 'bg-red-600 hover:bg-red-700'
                : mode === 'withdraw-inside' ? 'bg-orange-600 hover:bg-orange-700'
                : 'bg-amber-600 hover:bg-amber-700'
            }`}
            data-testid="cash-submit-btn"
          >
            {busy ? 'Saving...' : submitLabel}
          </button>
          {/* Tiny explanation strip so the user understands which counter
              this action moves before they click. */}
          <p className="basis-full text-[10px] text-muted-foreground italic mt-1">
            {mode === 'deposit-outside' && 'Outside cash counts toward Total Contributions.'}
            {mode === 'deposit-inside' && 'Inside cash (sale proceeds, dividends) does NOT count as a contribution.'}
            {mode === 'withdraw-outside' && 'Outside withdrawal — counted against Net Contributions (principal coming back out).'}
            {mode === 'withdraw-inside' && 'Inside withdrawal — drawing down dividends or sale proceeds. Does NOT reduce Net Contributions.'}
            {mode === 'remove' && (removeSource === 'outside' ? 'Outside removal cleanly reverses Total Contributions.'
              : removeSource === 'inside' ? 'Inside removal reduces cash only — no contribution effect.'
              : 'Split: outside-portion reverses contributions; inside-portion reduces cash only.')}
          </p>
          {err && <p className="w-full text-xs text-red-500">{err}</p>}
        </div>
      )}
    </div>
  );
}
